import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FaSignOutAlt, FaUsers, FaUserCircle, FaBox, FaSearch, FaList, FaBuilding, FaTruck, FaBoxOpen, FaShoppingCart, FaClipboardList, FaFileInvoiceDollar, FaCommentDots, FaRoute, FaBars, FaTimes, FaMapMarkerAlt, FaUserCheck, FaUndo, FaMoneyBillWave, FaRocket, FaStore, FaShoppingBag } from 'react-icons/fa';
import ReactCountryFlag from 'react-country-flag';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { usePlan } from '../hooks/usePlan';

const PLAN_FEATURE_MAP = {
  '/chatbot': 'ai_chatbot',
  '/warehouse-navigator': 'smart_navigation',
  '/product-search': 'voice_search',
};

const Navbar = ({ onOpenWizard }) => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingPaymentCount, setPendingPaymentCount] = useState(0);
  const planCtx = usePlan();

  useEffect(() => {
    if (!user) return;
    api.get('/returns/', { params: { status: 'processing_refund', page_size: 1 } }).then((res) => {
      const total = res.data.count ?? (Array.isArray(res.data) ? res.data.length : 0);
      setPendingPaymentCount(total);
    }).catch(() => {});
  }, [user]);

  const changeLanguage = (lng) => { i18n.changeLanguage(lng); };
  const isRtl = i18n.language === 'fa';

  const hasFeatureBySlug = (slug) => {
    if (!planCtx?.plan?.plan_snapshot?.features) return true;
    const val = planCtx.plan.plan_snapshot.features[slug];
    if (val === undefined || val === null) return false;
    return val === 'true' || val === true;
  };

  const links = [];
  if (user) {
    links.push(
      { to: '/', icon: null, label: t('dashboard') },
      { to: '/parts', icon: FaBox, label: t('products') },
      { to: '/product-search', icon: FaSearch, label: t('product_search'), feature: 'voice_search' },
      { to: '/warehouse-navigator', icon: FaRoute, label: t('warehouse_navigator'), feature: 'smart_navigation' },
      { to: '/chatbot', icon: FaCommentDots, label: t('chatbot'), feature: 'ai_chatbot' },
      { to: '/sales-insights', icon: null, label: 'دستیار فروش', feature: 'ai_chatbot' },
      { to: '/categories', icon: FaList, label: t('categories') },
      { to: '/warehouses', icon: FaBuilding, label: t('warehouses') },
      { to: '/suppliers', icon: FaTruck, label: t('suppliers') },
      { to: '/location-assign', icon: FaMapMarkerAlt, label: 'انتساب کالا' },
      { to: '/customers', icon: FaUsers, label: t('customers') },
      { to: '/sales-orders', icon: FaShoppingCart, label: t('sales_orders') },
      { to: '/online-orders', icon: FaStore, label: 'سفارشات آنلاین' },
      { to: '/goods-receipt-notes', icon: FaClipboardList, label: t('goods_receipt_notes') },
      { to: '/invoices', icon: FaFileInvoiceDollar, label: t('invoices') },
      { to: '/returns', icon: FaUndo, label: 'مرجوعی کالا' },
      { to: '/payments', icon: FaMoneyBillWave, label: 'پرداختی‌ها' },
    );
    links.push({ to: '/company-profile', icon: null, label: 'پلن و کیف پول' });
    const isManager = user?.is_superuser || user?.is_staff || user?.role === 'admin' || user?.role === 'manager';
    if (isManager) {
      links.push({ to: '/users', icon: FaUserCheck, label: t('users') });
    }
    if (user?.is_superuser) {
      links.push({ to: '/roles', icon: null, label: t('roles') });
      links.push({ to: '/admin/companies', icon: null, label: 'مدیریت شرکت‌ها' });
      links.push({ to: '/admin/sms-templates', icon: null, label: 'مدیریت پیامک‌ها' });
      links.push({ to: '/admin/logs', icon: null, label: 'لاگ‌ها' });
    }
    if (isManager) {
      links.push({ to: '/settings', icon: null, label: t('settings') });
      links.push({ to: '/plan-manager', icon: null, label: 'مدیریت پلن‌ها' });
    }
  }

  const needsActivation = planCtx?.plan?.status === 'suspended' && planCtx?.plan?.last_billing_date === null;

  const visibleLinks = links.filter(link => {
    if (needsActivation && link.to !== '/company-profile' && link.to !== '/profile') return false;
    if (!link.feature) return true;
    return hasFeatureBySlug(link.feature);
  });

  return (
    <header className="bg-gray-800 text-white shadow-md w-full">
      <div className="px-3 md:px-6 py-3">
        <div className="flex justify-between items-center w-full">
          <Link to="/" className="text-lg md:text-xl font-bold truncate text-white hover:text-gray-300">
            PartBin
          </Link>
          <div className="flex items-center gap-2 md:gap-4">
            {!user ? (
              <>
                <Link to="/login" className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1.5 px-3 rounded text-sm">{t('login')}</Link>
                <Link to="/register" className="bg-green-500 hover:bg-green-700 text-white font-bold py-1.5 px-3 rounded text-sm">{t('register')}</Link>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/profile" className="flex items-center text-white hover:text-gray-300 text-sm">
                    <FaUserCircle className="text-xl ms-1 md:ms-1" />
                    <span className="hidden md:inline font-semibold truncate max-w-[200px]">{t('welcome_user', { username: user.username })}</span>
                    {user.company_name && (
                      <span className="hidden md:inline text-xs text-gray-400 ms-1 truncate max-w-[160px]">({user.company_name})</span>
                    )}
                </Link>
                <button onClick={logout} className="bg-red-500 hover:bg-red-700 text-white font-bold py-1.5 px-3 rounded text-sm flex items-center">
                  <FaSignOutAlt className="me-1 md:me-2" /> <span className="hidden md:inline">{t('logout')}</span>
                </button>
              </div>
            )}
            <button onClick={() => changeLanguage('fa')} className={`p-1.5 rounded hover:bg-gray-700 text-xs ${i18n.language === 'fa' ? 'bg-gray-700' : ''}`}>
              <ReactCountryFlag countryCode="IR" svg className="inline-block" /> <span className="hidden md:inline">فارسی</span>
            </button>
            <button onClick={() => changeLanguage('en')} className={`p-1.5 rounded hover:bg-gray-700 text-xs ${i18n.language === 'en' ? 'bg-gray-700' : ''}`}>
              <ReactCountryFlag countryCode="US" svg className="inline-block" /> <span className="hidden md:inline">English</span>
            </button>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden p-2 rounded hover:bg-gray-700"
              aria-label="Toggle menu"
            >
              {menuOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
            </button>
          </div>
        </div>
      </div>
      {user && (
        <nav className={`${menuOpen ? 'block' : 'hidden'} md:block border-t border-gray-700`}>
          <ul className={`flex flex-col md:flex-row md:flex-wrap ${isRtl ? 'md:space-x-reverse' : ''} md:space-x-1 px-3 md:px-6 py-2 md:py-1 gap-1`}>
            <li>
              <button onClick={() => { setMenuOpen(false); onOpenWizard(); }}
                className="block px-3 py-2 rounded bg-gradient-to-l from-indigo-600 to-blue-600 text-white hover:from-indigo-500 hover:to-blue-500 text-sm whitespace-nowrap flex items-center gap-1.5 font-bold shadow">
                <FaRocket /> راه‌اندازی فروشگاه
              </button>
            </li>
            <li>
              <Link to={user?.company_slug ? `/store/${user.company_slug_en || user.company_slug}` : '/store'} onClick={() => setMenuOpen(false)}
                className="block px-3 py-2 rounded bg-gradient-to-l from-emerald-600 to-teal-500 text-white hover:from-emerald-500 hover:to-teal-400 text-sm whitespace-nowrap flex items-center gap-1.5 font-bold shadow">
                <FaStore /> فروشگاه اینترنتی
              </Link>
            </li>
            {visibleLinks.map((link) => {
              const Icon = link.icon;
              return (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    onClick={() => setMenuOpen(false)}
                    className="block px-3 py-2 rounded hover:bg-gray-700 text-sm whitespace-nowrap flex items-center gap-1.5"
                  >
                    {Icon && <Icon className="shrink-0" />} {link.label}
                    {link.to === '/payments' && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ${pendingPaymentCount > 0 ? 'bg-red-500 text-white' : 'bg-gray-600 text-gray-300'}`}>
                        {pendingPaymentCount}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      )}
    </header>
  );
};

export default Navbar;